import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    /** The free-text number stored on the booking (`bookingDetails.invoiceNumber`). */
    invoiceNumber: string | null | undefined;
    /** ADR-0012 Mode A: kolula issues the invoice (`useIsInvoiceIssuingTenant`). */
    isInvoiceIssuingTenant: boolean;
}

/**
 * Whether the booking detail shows the free-text invoice number line.
 *
 * That field exists for Mode C only: it records the number the tenant's OWN
 * system produced, so their books and our bookings can be reconciled (PO,
 * 2026-09-29). A Mode A tenant's numbers are in the invoice section, so an
 * empty line there only suggests something is missing. A number already
 * stored before the tenant switched to Mode A is still shown: it is history.
 */
export const showsManualInvoiceNumber = (
    invoiceNumber: string | null | undefined,
    isInvoiceIssuingTenant: boolean,
): boolean => !isInvoiceIssuingTenant || (invoiceNumber ?? '').trim().length > 0;

/** The booking detail's "Rechnungsnummer" line — the room and the venue screen share it. */
const ManualInvoiceNumber = ({ invoiceNumber, isInvoiceIssuingTenant }: Readonly<Props>) => {
    const { t } = useTranslation();
    if (!showsManualInvoiceNumber(invoiceNumber, isInvoiceIssuingTenant)) return null;
    const value = (invoiceNumber ?? '').trim();
    return (
        <p className={'flex-1 text-right text-foreground font-bold'}>
            {t('create_booking.reservation_details.invoice_nr')}:{' '}
            <span className={'font-normal'}>{value.length > 0 ? value : t('common.missing')}</span>
        </p>
    );
};

export default ManualInvoiceNumber;
